import {Container, ContainerModule, type interfaces} from "inversify";
import {Bindings} from "data/constants/bindings";
import {type IUserStore, UserStore} from "data/stores/user/user.store";
import {AuthController, type IAuthController} from "views/controllers/auth/auth.controller";
import {
	type IMyAccountController,
	MyAccountController,
} from "views/pages/my_account/my_account.controller";
import {type ILoginController, LoginController} from "views/pages/login/login.controller";
import {API_URL, CONTENT_JSON_URL, JSON_URL} from "data/constants";
import {type IJSONProvider, JSONProvider} from "data/providers/json/json.provider";
import {AuthApiProvider, type IAuthApiProvider} from "data/providers/api/auth.api.provider";
import {HttpClientFactory, type IHttpClientService} from "data/services/http";
import {CountriesStore, type ICountriesStore} from "data/stores/countries/countries.store";
import {RoundsStore, type IRoundsStore} from "data/stores/rounds/rounds.store";
import {PlayersStore, type IPlayersStore} from "data/stores/players/players.store";
import {SquadsStore, type ISquadsStore} from "data/stores/squads/squds.store";
import {
	type ISecretGateController,
	SecretGateController,
} from "views/controllers/secrete_gate/secret_gate.controller";
import {
	type ILocalizationStore,
	LocalizationStore,
} from "data/stores/localization/localization.store";
import {
	type IStaticContentStore,
	StaticContentStore,
} from "data/stores/static_content/static_content.store";
import {HelpController, type IHelpController} from "views/pages/help/help.controller";
import {
	HelpListController,
	type IHelpListController,
} from "views/components/help/help_list/help_list.controller";
import {
	ContactUsController,
	type IContactUsController,
} from "views/components/help/contact_us/contact_us.controller";
import {
	type ILiveScoreController,
	LiveScoreController,
} from "views/controllers/live_score/live_score.controller";
import {ChecksumStore, type IChecksumStore} from "data/stores/checksum/checksum.store";
import {
	type IRegistrationController,
	RegistrationController,
} from "views/pages/registration/registration.controller";
import {type IUserApiProvider, UserApiProvider} from "data/providers/api/user.api.provider";
import {
	type IPasswordApiProvider,
	PasswordApiProvider,
} from "data/providers/api/password.api.provider";
import {
	ForgotPasswordController,
	type IForgotPasswordController,
} from "views/pages/forgot_password/forgot_password.controller";
import {
	type IResetPasswordController,
	ResetPasswordController,
} from "views/pages/reset_password/reset_password.controller";
import {
	type ISessionController,
	SessionController,
} from "views/components/session/session.controller";
import {
	BootstrapController,
	type IBootstrapController,
} from "views/components/bootstrap/bootstrap.controller";
import {
	type ILocalizationController,
	LocalizationController,
} from "views/controllers/localization/localization.controller";
import {type IMenuStore, MenuStore} from "data/stores/menu/menu.store";
import {HeaderController, type IHeaderController} from "views/components/header/header.controller";
import {type IMenuController, MenuController} from "views/components/menu/menu.controller";
import {type IModalsStore, ModalsStore} from "data/stores/modals/modals.store";
import {type IModalsController, ModalsController} from "views/controllers/modals/modals.controller";
import {GameController, type IGameController} from "views/pages/game/game.controller";
import {type ITeamStore, TeamStore} from "data/stores/team/team.store";
import {
	type ITeamSelectorController,
	TeamSelectorController,
} from "views/components/teamSelector/team_selector.controller";
import {
	type IPlayerSlotController,
	PlayerSlotController,
} from "views/components/playerSlot/player_slot.controller";
import {
	type IRoundNavigatorController,
	RoundNavigatorController,
} from "views/components/roundNavigator/round_navigator.controller";
import {type ITeamApiProvider, TeamApiProvider} from "data/providers/api/team.api.provider";
import {
	type ISaveBarController,
	SaveBarController,
} from "views/components/saveBar/save_bar.controller";
import {type IPlayerPoolStore, PlayerPoolStore} from "data/stores/player_pool/player_pool.store";
import {
	type IPoolController,
	PoolController,
} from "views/components/playerPool/player_pool.controller";
import {
	type IUserBarController,
	UserBarController,
} from "views/components/user_bar/user_bar.controller";
import {
	type IPoolModalController,
	PoolModalController,
} from "views/components/playerPoolModal/pool_modal.controller";
import {
	CreateLeagueController,
	ICreateLeagueController,
} from "views/pages/create_league/create_league.controller";
import {ILeaguesStore, LeaguesStore} from "data/stores/leagues/leagues.store";
import {
	ILeagueInviteFormController,
	LeagueInviteFormController,
} from "views/components/league_invite_form/league_invite_form.controller";
import {ILeaguesApiProvider, LeaguesApiProvider} from "data/providers/api/leagues.api.provider";
import {
	IMyLeaguesController,
	MyLeaguesController,
} from "views/pages/my_leagues/my_leagues.controller";
import {
	IJoinCreateLeagueController,
	JoinCreateLeagueController,
} from "views/components/join_create_league_card/join_create_league_card.controller";
import {
	ILeagueCardController,
	LeagueCardController,
} from "views/components/league_card/league_card.controller";
import {IStatesStore, StatesStore} from "data/stores/states/states.store";
import {
	ILeaderboardController,
	LeaderboardController,
} from "views/pages/leaderboard/leaderboard.controller";
import {IRankingsApiProvider, RankingsApiProvider} from "data/providers/api/rankings.api.provider";
import {IRankingsStore, RankingsStore} from "data/stores/rankings/rankings.store";
import {ILeagueController, LeagueController} from "views/pages/league/league.controller";
import {
	ILeagueAboutController,
	LeagueAboutController,
} from "views/pages/league_about/league_about.controller";
import {
	ILeagueInviteController,
	LeagueInviteController,
} from "views/pages/league_invite/league_invite.controller";
import {
	ILeagueSettingsController,
	LeagueSettingsController,
} from "views/pages/league_settings/league_settings.controller";
import {
	type ILeagueStandingsController,
	LeagueStandingsController,
} from "views/pages/league_standings/league_standings.controller";
import {
	CookieBannerController,
	type ICookieBannerController,
} from "views/components/CookieBanner/cookie_banner.controller";
import {
	type IPlayedGamesController,
	PlayedGamesController,
} from "views/components/modals/played_game/played_game.controller";
import {
	type IJoinLeaguesController,
	JoinLeaguesController,
} from "views/pages/join_leagues/join_leagues.controller";
import {
	type ITiebreakerModalController,
	TiebreakerModalController,
} from "views/components/modals/tiebreaker_modal/tiebreaker_modal.controller";
import {
	IJoinLeagueCardController,
	JoinLeagueCardController,
} from "views/components/join_league_card/join_league_card.controller";
import {
	IInviteCreateLeagueCardController,
	InviteCreateLeagueCardController,
} from "views/components/invite_create_league_card/invite_create_league_card.controller";
import {
	IConfirmModalController,
	ConfirmModalController,
} from "views/components/modals/confirm_modal/confirm_modal.controller";
import {
	type IKeyIndicatorController,
	KeyIndicatorController,
} from "views/components/key_indicator/key_indicator.controller";
import {
	type ITeamSavedModalController,
	TeamSavedModalController,
} from "views/components/modals/team_saved_modal/team_saved_modal.controller";
import {
	type ITutorialModalController,
	TutorialModalController,
} from "views/components/modals/tutorial_modal/tutorial_modal.controller";
import {ILandingController, LandingController} from "views/pages/landing/landing.controller";
import {
	type IInfoTradeModalController,
	InfoTradeModalController,
} from "views/components/modals/info_trade_modal/info_trade_modal.controller";
import {
	type IPlayerPoolSlotController,
	PlayerPoolSlotController,
} from "views/components/playerPoolSlot/player_pool_slot.controller";
import {type IPrizeController, PrizeController} from "views/pages/prizes/prize.controller";
import {
	HowToScoreModalController,
	type IHowToScoreModalController,
} from "views/components/modals/how_to_score/how_to_score.controller";
import {
	type ISaveTeamModalController,
	SaveTeamModalController,
} from "views/components/modals/save_team/save_team.controller";
import {
	IPromptModalController,
	PromptModalController,
} from "views/components/modals/prompt_modal/prompt_modal.controller";
import {type ITradeApiProvider, TradeApiProvider} from "data/providers/api/trade.api.provider";
import {
	IRankingsTableController,
	RankingsTableController,
} from "views/components/rankings_table/rankings_table.controller";
import {
	type ITradesConfirmModalController,
	TradesConfirmModalController,
} from "views/components/modals/trades_confirm_modal/trades_confirm_modal.controller";
import {
	type IUserTeamController,
	UserTeamController,
} from "views/pages/user_team/user_team.controller";
import {
	type IUserTeamSelectorController,
	UserTeamSelectorController,
} from "views/components/user_team_selector/user_team_selector.controller";
import {FooterController, IFooterController} from "views/components/footer/footer.controller";
import {
	type IUserTeamRoundNavigatorController,
	UserTeamRoundNavigatorController,
} from "views/components/roundNavigator/user_team_round_navigator.controller";
import {
	IModalPlayerProfileController,
	ModalPlayerProfileController,
} from "views/components/modals/player_profile/player_profile.controller";
import {
	IPlayerProfileOverviewController,
	PlayerProfileOverviewController,
} from "views/components/player_profile_overview/player_profile_overview.controller";
import {
	IPlayerProfileSessionsController,
	PlayerProfileSessionsController,
} from "views/components/player_profile_sessions/player_profile_sessions.controller";
import {
	IPlayerProfileInfoController,
	PlayerProfileInfoController,
} from "views/components/player_profile_info/player_profile_info.controller";
import {
	IInfoSessionModalController,
	InfoSessionModalController,
} from "views/components/modals/info_session_modal/info_session_modal.controller";
import {
	ChangeCaptainModalController,
	type IChangeCaptainModalController,
} from "views/components/modals/change_captain/change_captain.controller";

export const DIContainer = new Container();

export const services = new ContainerModule((bind: interfaces.Bind) => {
	bind<IHttpClientService>(Bindings.ApiHTTPClient).toConstantValue(
		HttpClientFactory.createApiClient({
			baseURL: API_URL,
			withCredentials: true,
		})
	);

	bind<IHttpClientService>(Bindings.JsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: JSON_URL,
		})
	);

	bind<IHttpClientService>(Bindings.ContentJsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: CONTENT_JSON_URL,
		})
	);
});

export const providers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IJSONProvider>(Bindings.JSONProvider).to(JSONProvider);
	bind<IAuthApiProvider>(Bindings.AuthApiProvider).to(AuthApiProvider);
	bind<IUserApiProvider>(Bindings.UserApiProvider).to(UserApiProvider);
	bind<IPasswordApiProvider>(Bindings.PasswordApiProvider).to(PasswordApiProvider);
	bind<ITeamApiProvider>(Bindings.TeamApiProvider).to(TeamApiProvider);
	bind<ITradeApiProvider>(Bindings.TradeApiProvider).to(TradeApiProvider);
	bind<IRankingsApiProvider>(Bindings.RankingsApiProvider).to(RankingsApiProvider);
});

export const stores = new ContainerModule((bind: interfaces.Bind) => {
	bind<ILocalizationStore>(Bindings.LocalizationStore).to(LocalizationStore).inSingletonScope();
	bind<IUserStore>(Bindings.UserStore).to(UserStore).inSingletonScope();
	bind<IRoundsStore>(Bindings.RoundsStore).to(RoundsStore).inSingletonScope();
	bind<IPlayersStore>(Bindings.PlayersStore).to(PlayersStore).inSingletonScope();
	bind<ISquadsStore>(Bindings.SquadsStore).to(SquadsStore).inSingletonScope();
	bind<IMenuStore>(Bindings.MenuStore).to(MenuStore).inSingletonScope();
	bind<IModalsStore>(Bindings.ModalsStore).to(ModalsStore).inSingletonScope();
	bind<ITeamStore>(Bindings.TeamStore).to(TeamStore).inSingletonScope();
	bind<IPlayerPoolStore>(Bindings.PlayerPoolStore).to(PlayerPoolStore).inSingletonScope();

	bind<ICountriesStore>(Bindings.CountriesStore).to(CountriesStore);
	bind<IStatesStore>(Bindings.StatesStore).to(StatesStore);
	bind<IStaticContentStore>(Bindings.StaticContentStore).to(StaticContentStore);

	bind<IChecksumStore>(Bindings.ChecksumStore).to(ChecksumStore);
	bind<ILeaguesStore>(Bindings.LeaguesStore).to(LeaguesStore).inSingletonScope();
	bind<IRankingsStore>(Bindings.RankingsStore).to(RankingsStore).inSingletonScope();
});

export const controllers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IAuthController>(Bindings.AuthController).to(AuthController);
	bind<ILocalizationController>(Bindings.LocalizationController).to(LocalizationController);
	bind<ILoginController>(Bindings.LoginController).to(LoginController);
	bind<IForgotPasswordController>(Bindings.ForgotPasswordController).to(ForgotPasswordController);
	bind<IResetPasswordController>(Bindings.ResetPasswordController).to(ResetPasswordController);
	bind<IRegistrationController>(Bindings.RegistrationController).to(RegistrationController);
	bind<IMyAccountController>(Bindings.MyAccountController).to(MyAccountController);
	bind<ISecretGateController>(Bindings.SecretGateController).to(SecretGateController);
	bind<IHelpController>(Bindings.HelpController).to(HelpController);
	bind<IPrizeController>(Bindings.PrizeController).to(PrizeController);
	bind<IHelpListController>(Bindings.HelpListController).to(HelpListController);
	bind<IContactUsController>(Bindings.ContactUsController).to(ContactUsController);
	bind<ILiveScoreController>(Bindings.LiveScoreController).to(LiveScoreController);
	bind<ISessionController>(Bindings.SessionController).to(SessionController);
	bind<IBootstrapController>(Bindings.BootstrapController).to(BootstrapController);
	bind<IHeaderController>(Bindings.HeaderController).to(HeaderController);
	bind<IMenuController>(Bindings.MenuController).to(MenuController);
	bind<IModalsController>(Bindings.ModalsController).to(ModalsController);
	bind<IGameController>(Bindings.GameController).to(GameController);
	bind<ITeamSelectorController>(Bindings.TeamSelectorController).to(TeamSelectorController);
	bind<IPlayerSlotController>(Bindings.PlayerSlotController).to(PlayerSlotController);
	bind<IRoundNavigatorController>(Bindings.RoundNavigatorController).to(RoundNavigatorController);
	bind<ICookieBannerController>(Bindings.CookieBannerController).to(CookieBannerController);
	bind<ISaveBarController>(Bindings.SaveBarController).to(SaveBarController);
	bind<IPoolController>(Bindings.PoolController).to(PoolController);
	bind<IUserBarController>(Bindings.UserBarController).to(UserBarController);
	bind<IPoolModalController>(Bindings.PoolModalController).to(PoolModalController);
	bind<IPlayedGamesController>(Bindings.PlayedGamesController).to(PlayedGamesController);
	bind<ICreateLeagueController>(Bindings.CreateLeagueController).to(CreateLeagueController);
	bind<ILeagueInviteFormController>(Bindings.LeagueInviteFormController).to(
		LeagueInviteFormController
	);
	bind<ILeaguesApiProvider>(Bindings.LeaguesApiProvider).to(LeaguesApiProvider);
	bind<IMyLeaguesController>(Bindings.MyLeaguesController).to(MyLeaguesController);
	bind<IJoinCreateLeagueController>(Bindings.JoinCreateLeagueController).to(
		JoinCreateLeagueController
	);
	bind<ILeagueCardController>(Bindings.LeagueCardController).to(LeagueCardController);
	bind<IJoinLeagueCardController>(Bindings.JoinLeagueCardController).to(JoinLeagueCardController);
	bind<IInviteCreateLeagueCardController>(Bindings.InviteCreateLeagueCardController).to(
		InviteCreateLeagueCardController
	);
	bind<ILeaderboardController>(Bindings.LeaderboardController).to(LeaderboardController);
	bind<ILeagueController>(Bindings.LeagueController).to(LeagueController);
	bind<ILeagueAboutController>(Bindings.LeagueAboutController).to(LeagueAboutController);
	bind<ILeagueInviteController>(Bindings.LeagueInviteController).to(LeagueInviteController);
	bind<ILeagueSettingsController>(Bindings.LeagueSettingsController).to(LeagueSettingsController);
	bind<ILeagueStandingsController>(Bindings.LeagueStandingsController).to(
		LeagueStandingsController
	);
	bind<IJoinLeaguesController>(Bindings.JoinLeaguesController).to(JoinLeaguesController);
	bind<ITiebreakerModalController>(Bindings.TiebreakerModalController).to(
		TiebreakerModalController
	);
	bind<IConfirmModalController>(Bindings.ConfirmModalController).to(ConfirmModalController);
	bind<IKeyIndicatorController>(Bindings.KeyIndicatorController).to(KeyIndicatorController);
	bind<ITeamSavedModalController>(Bindings.TeamSavedModalController).to(TeamSavedModalController);
	bind<ITutorialModalController>(Bindings.TutorialModalController).to(TutorialModalController);
	bind<ILandingController>(Bindings.LandingController).to(LandingController);
	bind<IInfoTradeModalController>(Bindings.InfoTradeModalController).to(InfoTradeModalController);
	bind<IPlayerPoolSlotController>(Bindings.PlayerPoolSlotController).to(PlayerPoolSlotController);
	bind<IHowToScoreModalController>(Bindings.HowToScoreModalController).to(
		HowToScoreModalController
	);
	bind<ISaveTeamModalController>(Bindings.SaveTeamModalController).to(SaveTeamModalController);
	bind<IPromptModalController>(Bindings.PromptModalController).to(PromptModalController);
	bind<IRankingsTableController>(Bindings.RankingsTableController).to(RankingsTableController);
	bind<ITradesConfirmModalController>(Bindings.TradesConfirmModalController).to(
		TradesConfirmModalController
	);
	bind<IUserTeamController>(Bindings.UserTeamController).to(UserTeamController);
	bind<IUserTeamSelectorController>(Bindings.UserTeamSelectorController).to(
		UserTeamSelectorController
	);
	bind<IFooterController>(Bindings.FooterController).to(FooterController);
	bind<IUserTeamRoundNavigatorController>(Bindings.UserTeamRoundNavigatorController).to(
		UserTeamRoundNavigatorController
	);
	bind<IModalPlayerProfileController>(Bindings.ModalPlayerProfileController).to(
		ModalPlayerProfileController
	);
	bind<IPlayerProfileOverviewController>(Bindings.PlayerProfileOverviewController).to(
		PlayerProfileOverviewController
	);
	bind<IPlayerProfileSessionsController>(Bindings.PlayerProfileSessionsController).to(
		PlayerProfileSessionsController
	);
	bind<IPlayerProfileInfoController>(Bindings.PlayerProfileInfoController).to(
		PlayerProfileInfoController
	);
	bind<IInfoSessionModalController>(Bindings.InfoSessionModalController).to(
		InfoSessionModalController
	);
	bind<IChangeCaptainModalController>(Bindings.ChangeCaptainModalController).to(
		ChangeCaptainModalController
	);
});
